import React, { useId, useState } from 'react';
import { PASSWORD_MAX_LENGTH, PASSWORD_REQUIREMENTS } from '@shared/schemas/password.schema.js';
import { Button, Modal, ModalBody, ModalHeader } from '@/components/common';
import { useAppContext } from '@/hooks/useAppContext';
import { ApiRequestError } from '@/services/invitation-api';
import { PasswordRequirementsChecklist } from './PasswordRequirementsChecklist';
import { FaCheckCircle, FaExclamationTriangle, FaKey, FaLock, FaLockOpen } from 'react-icons/fa';

const DEAD_LINK = 'This password reset link is no longer valid';

interface ResetPasswordLandingProps {
  token: string;
  /** Leave this view: signed in after a reset, or back to sign-in. */
  onDone: () => void;
  /** Leave this view for the "Forgot password?" form, to ask for a fresh link. */
  onRequestNewLink: () => void;
}

type Stage = 'form' | 'done' | 'dead';

/**
 * The `/reset-password/<token>` screen: choose a new password, with the same
 * rules as signup. Success signs the user in. Every dead link — expired,
 * already used, superseded by a newer one — reads the same.
 */
export const ResetPasswordLanding: React.FC<ResetPasswordLandingProps> = ({ token, onDone, onRequestNewLink }) => {
  const { auth } = useAppContext();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ password?: string; confirmPassword?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>('form');
  const [isBusy, setIsBusy] = useState(false);

  const idPrefix = useId();
  const passwordId = `${idPrefix}-password`;
  const confirmId = `${idPrefix}-confirm`;
  const requirementsId = `${idPrefix}-requirements`;

  const validate = () => {
    const errors: typeof fieldErrors = {};
    if (!password) {
      errors.password = 'Password is required';
    } else if (PASSWORD_REQUIREMENTS.some(requirement => !requirement.test(password))) {
      errors.password = 'Password does not meet all requirements';
    } else if (password.length > PASSWORD_MAX_LENGTH) {
      errors.password = `Password must be at most ${PASSWORD_MAX_LENGTH} characters`;
    }
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (confirmPassword !== password) {
      errors.confirmPassword = 'Passwords do not match';
    }
    setFieldErrors(errors);
    return !errors.password && !errors.confirmPassword;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isBusy || !validate()) return;

    setError(null);
    setIsBusy(true);
    try {
      await auth.resetPassword(token, password);
      setStage('done');
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === 'password_reset_invalid') {
        setStage('dead');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to reset the password');
      }
    } finally {
      setIsBusy(false);
    }
  };

  const inputClass = (invalid: boolean) =>
    `w-full px-4 py-3 bg-game-cream border border-game-medium/50 rounded-lg text-gray-900 placeholder-gray-600 focus:border-game-golden focus:bg-game-cream/90 focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200 ${
      invalid ? 'border-game-orange bg-game-orange/20' : ''
    }`;

  return (
    <Modal
      isOpen={true}
      onClose={() => {}}
      size="md"
      showCloseButton={false}
      className="bg-gradient-to-b from-game-dark/95 via-game-dark/90 to-game-medium/30 backdrop-blur-md"
    >
      <ModalHeader showCloseButton={false}>
        <div className="text-center w-full">
          <div className="flex items-center justify-center mb-4">
            <img src="/assets/logo/minka-logo-color.png" alt="Minka" className="h-20 w-auto object-contain" />
          </div>
          <h2 className="text-xl font-bold text-white">Choose a new password</h2>
        </div>
      </ModalHeader>

      <ModalBody>
        {stage === 'dead' && (
          <div className="space-y-6">
            <div className="p-4 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg">
              <div className="flex items-center gap-3">
                <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
                <span className="font-medium">{DEAD_LINK}</span>
              </div>
              <p className="mt-2 text-sm text-gray-200">
                Links work for one hour and only once, and asking for a new link replaces the old one.
              </p>
            </div>
            <Button type="button" variant="primary" onClick={onRequestNewLink} className="w-full px-6 py-3">
              <span>Request a new link</span>
            </Button>
            <button
              type="button"
              onClick={onDone}
              className="w-full text-sm text-game-golden hover:text-game-accent underline-offset-2 hover:underline"
            >
              Back to sign in
            </button>
          </div>
        )}

        {stage === 'done' && (
          <div className="space-y-6 text-center">
            <div role="status" className="flex items-center justify-center gap-3 text-green-300">
              <FaCheckCircle />
              <span className="font-medium">Your password was changed and you are signed in.</span>
            </div>
            <p className="text-sm text-gray-200">Every other session of your account has been signed out.</p>
            <Button type="button" variant="primary" onClick={onDone} className="px-6 py-3">
              Continue to Minka
            </Button>
          </div>
        )}

        {stage === 'form' && (
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {error && (
              <div className="p-4 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg">
                <div className="flex items-center gap-3">
                  <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
                  <span className="font-medium">{error}</span>
                </div>
              </div>
            )}

            <div>
              <label htmlFor={passwordId} className="block text-sm font-semibold text-white mb-2">
                <div className="flex items-center gap-2">
                  <FaLock className="text-game-golden" />
                  New password
                </div>
              </label>
              <input
                type="password"
                id={passwordId}
                name="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: undefined }));
                }}
                required
                autoComplete="new-password"
                maxLength={PASSWORD_MAX_LENGTH}
                aria-describedby={requirementsId}
                className={inputClass(!!fieldErrors.password)}
                placeholder="Create a strong password"
              />
              {fieldErrors.password && (
                <div className="mt-2 p-2 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-md text-sm flex items-center gap-2">
                  <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
                  {fieldErrors.password}
                </div>
              )}
              <PasswordRequirementsChecklist id={requirementsId} password={password} />
            </div>

            <div>
              <label htmlFor={confirmId} className="block text-sm font-semibold text-white mb-2">
                <div className="flex items-center gap-2">
                  <FaLockOpen className="text-game-accent" />
                  Confirm new password
                </div>
              </label>
              <input
                type="password"
                id={confirmId}
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (fieldErrors.confirmPassword) setFieldErrors(prev => ({ ...prev, confirmPassword: undefined }));
                }}
                required
                autoComplete="new-password"
                maxLength={PASSWORD_MAX_LENGTH}
                className={inputClass(!!fieldErrors.confirmPassword)}
                placeholder="Confirm your new password"
              />
              {fieldErrors.confirmPassword && (
                <div className="mt-2 p-2 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-md text-sm flex items-center gap-2">
                  <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
                  {fieldErrors.confirmPassword}
                </div>
              )}
            </div>

            <Button type="submit" variant="primary" isLoading={isBusy} className="w-full px-6 py-3">
              <FaKey />
              <span>Set new password</span>
            </Button>
          </form>
        )}
      </ModalBody>
    </Modal>
  );
};
